/*
 *  Copyright 2026 LiteFarm.org
 *  This file is part of LiteFarm.
 *
 *  LiteFarm is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  LiteFarm is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 *  GNU General Public License for more details, see <https://www.gnu.org/licenses/>.
 */

/**
 * scanPassportSaga — Phase 8 / Plan 08-02
 *
 * Multipart upload pipeline for the Scan-Passport modal. Phase 6 multer route
 * accepts ONE file per request (.single('file')) — for multi-file batches we
 * POST sequentially and concatenate per-page arrays (see "Rule 1" in plan
 * 08-02 deviation rules).
 *
 * LD-22 phased-spinner contract: dispatches `processingStarted()` after each
 * file's upload promise resolves so the slice transitions
 *   'uploading' -> 'processing' -> 'reviewing' | 'error'
 *
 * D-15 / API-04 PII rule: NEVER `console.log` the OCR response body, file
 * blobs, or per-page mapped values. Only `console.error` of structured
 * `{ status, code, message }` metadata on failure.
 *
 * Progress granularity: file-completion ticks (Option B from plan 08-02). True
 * byte-level via redux-saga `eventChannel` is deferred to follow-up
 * `SCAN-PROGRESS-V2`.
 */

import { call, put, select, takeLatest } from 'redux-saga/effects';
import { createAction } from '@reduxjs/toolkit';
import { store } from '../../../store/store';
import { loginSelector } from '../../userFarmSlice';
import { axios, getHeader } from '../../saga';
import i18n from '../../../locales/i18n';
import {
  enqueueErrorSnackbar,
  enqueueSuccessSnackbar,
} from '../../Snackbar/snackbarSlice';
import { url } from '../../../apiConfig';
import { api } from '../../../store/api/apiSlice';
import {
  uploadStarted,
  uploadProgress,
  processingStarted,
  pagesReceived,
  scanFailed,
  closeModal,
  openModal,
  selectOriginalFiles,
  selectActivePages,
  selectSaveBlockedByP6,
  selectBulkFields,
  selectSaveBlockedByBulkFields,
  selectBlockedPageIndices,
  pagesSaved,
  savingStarted,
  saveSucceeded,
  saveFailed,
} from './scanPassportSlice';
import {
  buildAnimalCreatePayload,
  buildAnimalUpdatePayload,
  detectSilentlyDroppedFields,
  MISSING_FROM_PATCH_ALLOWLIST,
} from './buildAnimalPayloads';
import { expandFilesIntoChunks, DEFAULT_PAGES_PER_CHUNK } from './splitPdfIntoChunks';

// Public saga-listener action creators
/** @type {import('@reduxjs/toolkit').ActionCreatorWithPayload<{ files: File[] }, 'scanPassport/uploadFilesSaga'>} */
export const uploadFiles = createAction('scanPassport/uploadFilesSaga');
export const retakeScan = createAction('scanPassport/retakeScanSaga');
export const saveAll = createAction('scanPassport/saveAllSaga');

/**
 * uploadAndScanSaga
 *
 * @param {Object} action - { payload: { files: File[] } }
 *
 * Phase 6 multer config uses `.single('file')` — only one file per request.
 * Sequential POSTs + per-page array concat is the documented pattern.
 *
 * Progress: axios onUploadProgress dispatches uploadProgress directly via the
 * exported store (the callback is sync — can't yield put from inside).
 * When bytes hit 100% we also dispatch processingStarted so the UI swaps the
 * linear bar for the OCR spinner immediately, instead of stalling at ~100%
 * while axios awaits the server's OCR response (~20s).
 */
export function* uploadAndScanSaga({ payload }) {
  const files = payload?.files;
  if (!files || files.length === 0) return;

  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);
  const scanUrl = `${url}/animals/scan-passport/farm/${farm_id}`;

  // Cache the originalFiles in slice for LD-21 Retake (Plan 08-04 will consume).
  // Cache the user-selected originals (not chunks) so Retake replays the full
  // batch from the picker's perspective.
  yield put(uploadStarted({ files }));

  // Expand any PDF > DEFAULT_PAGES_PER_CHUNK pages into smaller PDFs. Images
  // and small PDFs pass through unchanged. This lets us handle arbitrarily
  // large passport PDFs without hitting the 200 MB per-request multer cap
  // and gives the progress bar real chunk-level granularity instead of one
  // long stall while the server OCRs 500+ pages.
  let uploadList;
  try {
    uploadList = yield call(expandFilesIntoChunks, files, DEFAULT_PAGES_PER_CHUNK);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('scanPassport pdf-chunk failed', { message: err?.message });
    yield put(
      scanFailed({
        code: 'PDF_CHUNK_FAILED',
        message: err?.message ?? 'PDF split failed',
      }),
    );
    yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.SAVE_FAILED')));
    return;
  }

  const totalChunks = uploadList.length;
  let chunksDone = 0;
  let allPages = [];

  try {
    for (const file of uploadList) {
      const formData = new FormData();
      formData.append('file', file);

      const chunkIndex = chunksDone;
      let bytesComplete = false;
      const response = yield call(axios.post, scanUrl, formData, {
        ...header,
        headers: {
          ...header.headers,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (e) => {
          if (!e.total) return;
          const fileFraction = e.loaded / e.total;
          const overall = Math.round(((chunkIndex + fileFraction) / totalChunks) * 100);
          store.dispatch(uploadProgress(overall));
          if (!bytesComplete && fileFraction >= 1) {
            bytesComplete = true;
            store.dispatch(processingStarted());
          }
        },
      });

      // P-7 / LD-22 transition: after each file's upload resolves, the saga is
      // effectively past the SEND phase for that file. Flip to 'processing'
      // so the spinner copy switches from "Uploading X%" to "Reading passport…".
      // (Idempotent — slice ignores re-entry from 'processing'.)
      yield put(processingStarted());

      if (Array.isArray(response?.data)) {
        const tagged = response.data.map((page) => ({
          ...page,
          _source_file_index: chunksDone,
        }));
        allPages = allPages.concat(tagged);
      }

      chunksDone += 1;
      // Floor to integer percent of completed chunks (onUploadProgress may
      // have emitted intermediate values for the current chunk already).
      yield put(uploadProgress(Math.round((chunksDone / totalChunks) * 100)));
    }

    yield put(pagesReceived(allPages));
  } catch (err) {
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    // PII rule (D-15 / P-5): only structured metadata, NEVER err.response.data
    // or the File payload.
    // eslint-disable-next-line no-console
    console.error('scanPassport upload failed', {
      status,
      code,
      message: err?.message,
    });

    if (status === 503 || code === 'OCR_SERVICE_UNAVAILABLE') {
      yield put(
        scanFailed({
          code: 'OCR_SERVICE_UNAVAILABLE',
          message: 'OCR service unavailable',
        }),
      );
      yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.OCR_UNAVAILABLE')));
    } else {
      yield put(
        scanFailed({
          code: code ?? 'SCAN_FAILED',
          message: err?.message ?? 'Scan failed',
        }),
      );
      yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.SAVE_FAILED')));
    }
  }
}

/**
 * retakeScanSaga — Phase 8 / Plan 08-04 (LD-21 whole-batch retake)
 *
 * Triggered by the Retake button on any failed/degraded review card. Re-uploads
 * the WHOLE original batch (NOT per-page) — the cached `originalFiles` from the
 * slice (populated by `uploadStarted` during the first upload pass) is replayed
 * through the same upload pipeline.
 *
 * Sequence:
 *   1. Read originalFiles from slice via selectOriginalFiles.
 *   2. If empty (cache was cleared, e.g. user closed and reopened the modal),
 *      dispatch an error snackbar and abort.
 *   3. Dispatch closeModal() — wipes the queue + status back to initialState.
 *   4. Dispatch openModal() — re-opens the dialog so the user stays in flow.
 *   5. Dispatch uploadFiles({ files }) — re-fires the upload saga with the
 *      cached File objects.
 *
 * Trade-off (accepted per LD-21): any per-page state (skipped, mode='update',
 * mode='new_with_override') is discarded. User explicitly chose Retake.
 *
 * Note: closeModal() RESETS the slice to initialState — including originalFiles.
 * We read `originalFiles` BEFORE dispatching closeModal so the cached array is
 * captured in this saga's local scope. The subsequent uploadFiles dispatch
 * re-populates `originalFiles` via the uploadStarted reducer.
 */
export function* retakeScanSaga() {
  const originalFiles = yield select(selectOriginalFiles);

  if (!originalFiles || originalFiles.length === 0) {
    yield put(
      enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.OCR_NO_FIELDS')),
    );
    return;
  }

  // LD-21: close + reopen + re-upload. closeModal resets the slice, so we MUST
  // have captured `originalFiles` above before dispatching it.
  yield put(closeModal());
  yield put(openModal());
  yield put(uploadFiles({ files: originalFiles }));
}

/**
 * saveAllSaga — Phase 8 / Plan 08-05 (UI-07, LD-20).
 *
 * Atomic-equivalent save-all over the active review queue. Per LD-20 this
 * REUSES the existing POST /animals (array body, knex transaction) — no new
 * batch endpoint. Per D-4 sequencing, new pages POST first, then update pages
 * PATCH. POST failure aborts before PATCH so partial cross-request commits
 * are minimized.
 *
 * Contract:
 *   1. Read selectActivePages — already excludes skipped + already-saved
 *      pages (MED-1 retry dedup).
 *   2. Empty → error snackbar, return (caller can re-click after fixing).
 *   3. P-6 blocked (any new_with_override page lacks zivotni_broj) → dispatch
 *      saveFailed VALIDATION_BLOCKED + error snackbar, return. Plan 08-04
 *      surfaces inline; this is the programmatic enforcement.
 *   4. Partition by mode: 'new' OR 'new_with_override' → POST /animals;
 *      'update' → PATCH /animals.
 *   5. Sequence POST → pagesSaved(POSTed indices) → PATCH → pagesSaved(PATCHed
 *      indices). Failure of either flips saveFailed.
 *   6. After PATCH success, MED-4 V-PATCH-FIELDS: detect Croatian fields that
 *      were silently dropped by the backend allowlist and surface
 *      ANIMAL.SCAN_PASSPORT.WARN.UPDATE_PARTIAL as a follow-up snackbar.
 *   7. On full success: dispatch saveSucceeded (slice resets), success
 *      snackbar with pluralized count, invalidate RTK 'Animals' tag so
 *      Inventory page refetches.
 *
 * PII rule (D-15 / T-08-05-02): catch block logs ONLY { status, code, message }
 * — NEVER payload, NEVER err.response.data.
 */
export function* saveAllSaga() {
  const active = yield select(selectActivePages);
  if (active.length === 0) {
    yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.SAVE_FAILED')));
    return;
  }

  const blocked = yield select(selectSaveBlockedByP6);
  if (blocked) {
    const indices = yield select(selectBlockedPageIndices);
    yield put(
      saveFailed({
        code: 'VALIDATION_BLOCKED',
        message: `Pages ${indices.join(', ')} need zivotni_broj before saving`,
      }),
    );
    yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.VALIDATION_FAILED')));
    return;
  }

  // Bulk-fields gate: vrsta dolaska + datum dolaska are required-manual globals.
  const bulkBlocked = yield select(selectSaveBlockedByBulkFields);
  if (bulkBlocked) {
    yield put(
      saveFailed({
        code: 'BULK_FIELDS_REQUIRED',
        message: 'Vrsta dolaska + Datum dolaska must be filled before saving',
      }),
    );
    yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.VALIDATION_FAILED')));
    return;
  }

  const bulkFields = yield select(selectBulkFields);

  // Strip empty strings (and null/undefined) before spreading. location_id is
  // optional — sending '' would fail backend FK validation since the column is
  // a UUID. The user can assign location later via the 'move animals' task.
  const cleanBulk = Object.fromEntries(
    Object.entries(bulkFields).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  );

  // Merge bulkFields + the hardcoded CATTLE default_type_id (passport-scan flow
  // is implicitly for cattle; the passport itself doesn't carry a type field,
  // so injecting it here keeps the OCR-mapper agnostic). Local DB seed: CATTLE
  // is default_animal_type.id = 1 (verified 2026-05-22).
  const withBulk = (p) => ({
    ...p,
    formValues: {
      ...(p.formValues || {}),
      ...cleanBulk,
      default_type_id: 1,
    },
  });

  const newPages = active
    .filter((p) => p.mode === 'new' || p.mode === 'new_with_override')
    .map(withBulk);
  const updatePages = active.filter((p) => p.mode === 'update').map(withBulk);

  const { user_id, farm_id } = yield select(loginSelector);
  const header = getHeader(user_id, farm_id);

  yield put(savingStarted());

  // MED-4 V-PATCH-FIELDS pre-check (LOCKED constant from buildAnimalPayloads):
  // collect the set of Croatian fields that were silently dropped across all
  // update pages so we surface a single warn snackbar after PATCH success
  // (instead of N snackbars, one per page).
  const droppedFieldsAcrossUpdates = new Set();
  for (const p of updatePages) {
    for (const f of detectSilentlyDroppedFields(p)) {
      droppedFieldsAcrossUpdates.add(f);
    }
  }

  try {
    if (newPages.length > 0) {
      const payload = newPages.map(buildAnimalCreatePayload);
      // PII rule (D-15): NEVER log payload — only structured metadata.
      yield call(axios.post, `${url}/animals`, payload, header);
      // MED-1 partial-success dedup: mark POST'd pages BEFORE attempting PATCH
      // so a PATCH failure followed by a user retry does NOT re-POST the
      // already-committed animals (which would 23505-conflict on the
      // (farm_id, zivotni_broj) unique partial index from Phase 6 DB-02).
      yield put(pagesSaved({ pageIndices: newPages.map((p) => p.pageIndex) }));
    }
    if (updatePages.length > 0) {
      const payload = updatePages.map(buildAnimalUpdatePayload);
      yield call(axios.patch, `${url}/animals`, payload, header);
      yield put(pagesSaved({ pageIndices: updatePages.map((p) => p.pageIndex) }));
    }

    // V-3-resolved RTK Query refetch — getAnimals provides ['Animals'] tag at
    // apiSlice.ts:117; invalidateTags is the canonical post-mutation pattern.
    yield put(api.util.invalidateTags(['Animals']));

    const totalSaved = newPages.length + updatePages.length;
    yield put(saveSucceeded());
    yield put(
      enqueueSuccessSnackbar(
        i18n.t('ANIMAL.SCAN_PASSPORT.SUCCESS.SAVED', { count: totalSaved }),
      ),
    );

    // MED-4 silent-data-loss warning AFTER success: if any update page carried
    // non-empty values in the Croatian MISSING_FROM_PATCH_ALLOWLIST fields,
    // the backend silently dropped them. Surface a persistent warn snackbar
    // so the user knows to manually re-enter those fields via the Inventory
    // edit form until BACKEND-PATCH-CROATIAN-FIELDS gap-closure ships.
    if (droppedFieldsAcrossUpdates.size > 0) {
      yield put(
        enqueueErrorSnackbar(
          i18n.t('ANIMAL.SCAN_PASSPORT.WARN.UPDATE_PARTIAL', {
            fields: Array.from(droppedFieldsAcrossUpdates).join(', '),
          }),
        ),
      );
    }
  } catch (err) {
    const status = err?.response?.status;
    const code = err?.response?.data?.code;
    // PII rule (T-08-05-02): only structured metadata, NEVER err.response.data
    // or the payload.
    // eslint-disable-next-line no-console
    console.error('scanPassport saveAll failed', {
      status,
      code,
      message: err?.message,
    });
    yield put(
      saveFailed({
        code: code ?? 'SAVE_FAILED',
        message: err?.message ?? 'Save failed',
      }),
    );
    yield put(enqueueErrorSnackbar(i18n.t('ANIMAL.SCAN_PASSPORT.ERROR.SAVE_FAILED')));
  }
}

export default function* scanPassportSaga() {
  yield takeLatest(uploadFiles.type, uploadAndScanSaga);
  yield takeLatest(retakeScan.type, retakeScanSaga);
  yield takeLatest(saveAll.type, saveAllSaga);
}
